import { LxClickAbleIcon } from '@components/icon/clickAbleIcon.tsx'
import { EditIcon, MoreActionsIcon, PlusIcon } from '@icons/utils'
import { LxDropdown } from '@components/select/dropdown.tsx'
import styles from './AccountActions.module.scss'
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useModal } from '@logic/contexts/Modal/ModalContext.tsx'
import { FilterObject } from '@logic/useFiltering.hook.ts'
import { LxIcon } from '@components/icon/Icon.tsx'
import { AccountEdit } from '../Edit/AccountEdit'
import CreateUser from '../User/CreateorEdit/CreateUser'
import { UserF } from '@src/logic/contexts/Users/UsersFrontend.type'
import { useNotify } from 'react-admin'

interface Account {
  accountId: string
  accountName: string
  [key: string]: any
}

interface ExtendedOption extends FilterObject<ReactNode> {
  disabled?: boolean
  disabledMessage?: string
  isLink?: boolean
}

const OptionItem = React.memo<{ icon: ReactNode; label: string; disabled?: boolean }>(({ icon, label, disabled }) => (
  <div className={disabled ? styles.dropdownOptionDisabled : styles.dropdownOption}>
    <LxIcon icon={icon} />
    <span>{label}</span>
  </div>
))

const generateOptions = (account: Account, currentUser: UserF, canEditAccount: boolean, canEditGlobalAccount: boolean, onUpdated?: () => void): ExtendedOption[] => {
  const canShowOptions = canEditGlobalAccount || (canEditAccount && currentUser.accountId === account.accountId)

  return [
    canShowOptions
      ? {
          value: <AccountEdit account={account} onUpdated={onUpdated} />,
          optionToRender: <OptionItem icon={EditIcon} label='Edit Account' />,
        }
      : {
          value: undefined,
          disabled: true,
          disabledMessage: 'You don’t have permission to manage this account',
          optionToRender: <OptionItem icon={EditIcon} label='Edit Account' disabled />,
        },

    canShowOptions
      ? {
          value: <CreateUser accountId={account.accountId} accountName={account.accountName} />,
          optionToRender: <OptionItem icon={PlusIcon} label='Add User' />,
        }
      : {
          value: undefined,
          disabled: true,
          disabledMessage: 'You don’t have permission to add users to this account',
          optionToRender: <OptionItem icon={PlusIcon} label='Add User' disabled />,
        },
  ]
}

interface AccountActionsProps {
  account: Account
  canEditAccount: boolean
  canEditGlobalAccount: boolean
  currentUser: UserF
  onUpdated?: () => void
  isLastChild?: boolean
}

export const AccountActions: React.FC<AccountActionsProps> = React.memo(({ account, canEditAccount, canEditGlobalAccount, currentUser, isLastChild, onUpdated }) => {
  const [isEditDropdownOpen, setIsEditDropdownOpen] = useState(false)
  const { showModal } = useModal()
  const notify = useNotify()
  const selectRef = useRef<HTMLDivElement>(null)

  const options = useMemo(() => {
    return generateOptions(account, currentUser, canEditAccount, canEditGlobalAccount, onUpdated)
  }, [account.accountId, account.accountName, currentUser.accountId, canEditAccount, canEditGlobalAccount, onUpdated])

  useEffect(() => {
    if (!isEditDropdownOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsEditDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isEditDropdownOpen])

  const handleOptionClick = useCallback(
    (option: ExtendedOption) => {
      if (option.disabled) {
        notify(option.disabledMessage || 'Action not available', { type: 'warning' })
        setIsEditDropdownOpen(false)
        return
      }

      if (option.value) {
        showModal(option.value)
        setIsEditDropdownOpen(false)
      }
    },
    [notify, showModal]
  )

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      setIsEditDropdownOpen(false)
    }
  }, [])

  const optionRenderer = useCallback((option: AccountOption) => option.optionToRender, [])

  return (
    <div ref={selectRef} onKeyDown={handleKeyDown} className={styles.accountActions}>
      <LxClickAbleIcon
        icon={MoreActionsIcon}
        dropdown={
          isEditDropdownOpen ? (
            <LxDropdown<ReactNode> className={styles.dropdown} options={options} optionRenderer={optionRenderer} onOptionClick={handleOptionClick} isLastChild={isLastChild} />
          ) : null
        }
        onClick={() => setIsEditDropdownOpen((prev) => !prev)}
      />
    </div>
  )
})
